import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, NAV_LINKS } from "../../lib/site";
import { scrollToId } from "./SmoothScroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleNav = (link) => {
    setOpen(false);
    if (link.to === "/careers") {
      navigate("/careers");
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.hash === "top" ? "top" : link.hash), 300);
    } else {
      scrollToId(link.hash === "top" ? "top" : link.hash);
    }
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? "bg-forest/80 backdrop-blur-xl border-b border-white/10 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto max-w-[1400px] px-5 sm:px-8 flex items-center justify-between gap-4">
        <button
          onClick={() => handleNav(NAV_LINKS[0])}
          className="flex items-center gap-3 shrink-0"
          data-testid="nav-logo"
          aria-label="Bezalel Property Services home"
        >
          <img src="/logo-white.png" alt="Bezalel Property Services" className="h-10 sm:h-12 w-auto object-contain" />
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link)}
              className="group relative font-display uppercase tracking-[0.14em] text-sm text-cream/85 hover:text-cream transition-colors duration-300"
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {link.label}
              <span className="absolute -bottom-1.5 left-0 h-[2px] w-0 bg-brand-orange transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${CONTACT.phoneTel}`}
            className="hidden sm:inline-flex items-center gap-2 h-12 px-5 rounded-full bg-brand-orange hover:bg-brand-orangeHover text-white font-display uppercase tracking-wider text-sm transition-[background-color,transform] duration-300 hover:-translate-y-0.5"
            data-testid="nav-call-now"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            Call Now
          </a>
          <button
            onClick={() => setOpen(true)}
            className="lg:hidden grid place-items-center h-12 w-12 rounded-full border border-white/15 text-cream"
            data-testid="mobile-menu-open"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-forest-deep/98 backdrop-blur-md lg:hidden"
            data-testid="mobile-menu-panel"
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-white/10">
              <img src="/logo-white.png" alt="Bezalel Property Services" className="h-10 w-auto object-contain" />
              <button
                onClick={() => setOpen(false)}
                className="grid place-items-center h-12 w-12 rounded-full border border-white/15 text-cream"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="flex flex-col px-6 pt-10 gap-2">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.label}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + i * 0.08 }}
                  onClick={() => handleNav(link)}
                  className="text-left font-display uppercase tracking-tight text-4xl py-3 text-cream border-b border-white/5"
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </motion.button>
              ))}
              <a
                href={`tel:${CONTACT.phoneTel}`}
                className="mt-8 inline-flex items-center justify-center gap-2 h-14 rounded-full bg-brand-orange text-white font-display uppercase tracking-wider text-lg"
                data-testid="mobile-call-now"
              >
                <Phone className="h-5 w-5" /> {CONTACT.phoneDisplay}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
